import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { SideDrawer } from 'src/components/side-drawer/side-drawer.component'
import { DrawerSection } from 'src/components/side-drawer/drawer-section.component'
import { useNotification } from 'src/providers/notification.provider'
import { TNavigationLocales } from 'src/types/locales'
import { twMerge } from 'src/utils/tw-merge.util'
import { TPatientFollowupDocument } from 'src/pages/patients/patient-file/patient-followup/_components/patient-forms/use-patient-followup-documents.hook'
import {
  getPatientFollowupDocumentName,
  hasPatientFollowupDocumentFile,
  isPatientFollowupDocumentAvailable,
  usePatientFollowupDocumentDisplay,
} from 'src/pages/patients/patient-file/patient-followup/_components/patient-forms/use-patient-followup-document-display.hook'
import { DocumentCard } from './document-card.component'
import { DocumentPreview } from './document-preview.component'
import { DocumentSubtitle } from './document-subtitle.component'
import { downloadPatientFollowupDocument } from './download-patient-followup-document.util'

interface PatientDocumentsDrawerProps {
  documents: TPatientFollowupDocument[]
  patientId: string | number
  projectId: string | number
  onClose: VoidFunction
}

const PatientDocumentPreviewSubtitle = ({ document }: { document: TPatientFollowupDocument }) => {
  const { dateLabel } = usePatientFollowupDocumentDisplay(document)

  return dateLabel ? <DocumentSubtitle>{dateLabel}</DocumentSubtitle> : null
}

interface PatientDocumentCardProps {
  document: TPatientFollowupDocument
  isSelected: boolean
  isDownloading: boolean
  onPreview: VoidFunction
  onDownload: VoidFunction
}

const PatientDocumentCard = ({
  document,
  isSelected,
  isDownloading,
  onPreview,
  onDownload,
}: PatientDocumentCardProps) => {
  const { t } = useTranslation()
  const { name, statusLabel, dateLabel, isAvailable, hasFile } = usePatientFollowupDocumentDisplay(document)

  return (
    <DocumentCard
      name={name}
      statusLabel={statusLabel}
      isStatusAvailable={isAvailable}
      subtitle={dateLabel}
      isSelected={isSelected}
      actions={
        hasFile
          ? {
              previewLabel: t('components.patient-documents-modal.visualize'),
              downloadLabel: t('components.patient-documents-modal.column-download'),
              isDownloading,
              onPreview,
              onDownload,
            }
          : undefined
      }
    />
  )
}

const PatientDocumentsDrawer = ({
  documents,
  patientId,
  projectId,
  onClose,
}: PatientDocumentsDrawerProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language as TNavigationLocales
  const { notify } = useNotification()
  // The previewed document is kept once the preview is closed, so it stays rendered while the drawer shrinks.
  const [previewedVariableId, setPreviewedVariableId] = useState<number | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [downloadingVariableIds, setDownloadingVariableIds] = useState<number[]>([])

  const availableDocuments = documents.filter(isPatientFollowupDocumentAvailable)
  const areAllDocumentsAvailable = availableDocuments.length === documents.length
  const downloadableDocuments = documents.filter(hasPatientFollowupDocumentFile)
  const previewedDocument = documents.find((document) => document.variableId === previewedVariableId)

  const togglePreview = (variableId: number) => {
    if (isPreviewOpen && previewedVariableId === variableId) {
      setIsPreviewOpen(false)
      return
    }
    setPreviewedVariableId(variableId)
    setIsPreviewOpen(true)
  }

  const download = async (document: TPatientFollowupDocument) => {
    setDownloadingVariableIds((current) => [...current, document.variableId])
    try {
      await downloadPatientFollowupDocument(
        document,
        getPatientFollowupDocumentName(document, locale),
        patientId,
        projectId,
      )
    } catch {
      notify({
        title: t('common.actions.error'),
        message: t('components.patient-documents-modal.download-error'),
        appearance: ECurecallNotificationAppearance.WARNING,
      })
    } finally {
      setDownloadingVariableIds((current) =>
        current.filter((variableId) => variableId !== document.variableId),
      )
    }
  }

  return (
    <SideDrawer
      title={t('components.patient-documents-drawer.title')}
      closeLabel={t('common.actions.close')}
      onClose={onClose}
      isDetailOpen={isPreviewOpen}
      detail={
        previewedDocument && (
          <DocumentPreview
            document={previewedDocument}
            name={getPatientFollowupDocumentName(previewedDocument, locale)}
            patientId={patientId}
            projectId={projectId}
          />
        )
      }
      detailTitle={previewedDocument && getPatientFollowupDocumentName(previewedDocument, locale)}
      detailSubtitle={previewedDocument && <PatientDocumentPreviewSubtitle document={previewedDocument} />}
      onDetailClose={() => setIsPreviewOpen(false)}
    >
      <DrawerSection
        title={t('components.patient-documents-drawer.section-title')}
        count={`${availableDocuments.length}/${documents.length}`}
        action={
          <button
            type="button"
            disabled={!areAllDocumentsAvailable || downloadableDocuments.length === 0}
            onClick={() => downloadableDocuments.forEach(download)}
            className={twMerge(
              'c-text-sm c-font-semibold c-leading-[19px] c-text-document-action hover:c-underline',
              (!areAllDocumentsAvailable || downloadableDocuments.length === 0) &&
                'c-cursor-not-allowed c-text-document-icon/50 hover:c-no-underline',
            )}
          >
            {t('components.patient-documents-drawer.download-all')}
          </button>
        }
      >
        {documents.map((document) => (
          <PatientDocumentCard
            key={document.variableId}
            document={document}
            isSelected={isPreviewOpen && document.variableId === previewedVariableId}
            isDownloading={downloadingVariableIds.includes(document.variableId)}
            onPreview={() => togglePreview(document.variableId)}
            onDownload={() => download(document)}
          />
        ))}
      </DrawerSection>
    </SideDrawer>
  )
}

export { PatientDocumentsDrawer }
